<template>
  <modal
    :id="modalId"
    :title="$t('add_new_product', { product: productLabel })"
    @close="close"
    ref="modal"
    :is-closeable="true"
    is-simplified-modal
    :show-footer="false"
  >
    <template v-slot:body>
      <div class="pap-body">
        <!-- Product name -->
        <form-group
          :label="$t('add_actor_product_name', { product: productLabel })"
          :stable-height="true"
          :errors="errors.name"
        >
          <ds-input
            is-simplified
            v-model="form.name"
            :placeholder="$t('add_actor_name_of_actor', { actor: productLabel })"
            @input="errors.name = null"
            :autofocus="true"
          />
        </form-group>

        <form-group
          v-if="nonProductClaims.length > 0"
          :label="$t('link_actor', 'Link to organisation')"
          :stable-height="true"
          :errors="errors.parentId ? [errors.parentId] : []"
        >
          <multiselect
            :model-value="selectedParent"
            :options="nonProductClaims"
            label="name"
            track-by="id"
            :searchable="false"
            :show-labels="false"
            :allow-empty="false"
            :disabled="nonProductClaims.length === 1"
            class="multiselect--datascouts is-simplified"
            @select="opt => { selectedParentId = opt.id; errors.parentId = null }"
          >
            <template #singleLabel="{ option }">
              <div class="pap-actor-option">
                <actor-avatar :actor="option" :width="20"/>
                <span>{{ option.name }}</span>
              </div>
            </template>
            <template #option="{ option }">
              <div class="pap-actor-option">
                <actor-avatar :actor="option" :width="20"/>
                <span>{{ option.name }}</span>
              </div>
            </template>
          </multiselect>
        </form-group>

        <!-- Copy contact details from parent -->
        <checkbox
          v-if="selectedParent"
          inline
          :label="$t('profile_use_same_address', { parentName: selectedParent?.name }, `Use same address as ${selectedParent ? selectedParent?.name : ''}`)"
          v-model="keepAddress"
        />

        <!-- URL -->
        <form-group
          :label="$t('add_actor_company_url', 'Website URL')"
          :stable-height="true"
          :errors="errors.url"
        >
          <ds-input
            is-simplified
            v-model="form.url"
            placeholder="https://"
            @input="errors.url = null"
          />
        </form-group>

        <!-- Category -->
        <form-group
          v-if="categoryOptions.length"
          :label="getTaxonomyAliasWithDefault('category')"
          :stable-height="true"
        >
          <dropdown
            is-simplified
            force
            :options="categoryOptions"
            v-model="form.category"
          />
        </form-group>
      </div>
      <div class="p-4 tw-flex flex-row justify-end">
        <ds-button
          variant="rounded-minimal"
          size="small"
          :label="$t('add_actor_cancel_button')"
          :disabled="busy"
          @click="close"
        />
        <ds-button
          variant="rounded"
          :label="$t('add_product_add_button', { product: productLabel })"
          :icon="busy ? 'spinner' : 'plus'"
          :disabled="busy"
          size="small"
          @click="submit"
        />
      </div>
    </template>

    <template v-slot:footer>
    </template>
  </modal>
</template>

<script>
  import Modal from './Modal.vue'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import ConfigMixin from '@/util/ConfigMixin'
  import { fetchProfile } from '@/api/user.js'
  import { trackHeapEvent } from '@/util/analytics.js'
  import { createActor, updateActor } from '@/api/actors.js'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import Checkbox from '@/components/Form/Checkbox.vue'
  import Multiselect from 'vue-multiselect'
  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'

  export default {
    name: 'ProfileAddProductModal',
    mixins: [ConfigMixin],
    components: { Modal, Dropdown, Checkbox, Multiselect, ActorAvatar },

    data () {
      return {
        modalId: MODAL_IDS.ADD_PRODUCT_FROM_PROFILE,
        busy: false,
        form: {
          name: '',
          url: '',
          category: null,
        },
        selectedParentId: null,
        keepAddress: false,
        errors: {},
      }
    },

    computed: {
      userProfile () {
        return this.$store.state.user.profile
      },
      nonProductClaims () {
        if (!this.userProfile?.claims) return []
        return this.userProfile.claims.filter(c => c.actor_type !== 'Product')
      },
      selectedParent () {
        if (!this.selectedParentId) return null
        return this.nonProductClaims.find(a => a.id === this.selectedParentId) || null
      },
      showKeepAddressCheckbox () {
        return this.selectedParent && (
          (this.selectedParent.address && (this.selectedParent.address.city || this.selectedParent.address.street)) ||
          this.selectedParent.phone ||
          this.selectedParent.email
        )
      },
      categoryOptions () {
        const list = this.categoryValuesAndLabelsPerType?.['Product'] || []
        return list
          .slice()
          .sort((a, b) => a.label > b.label ? 1 : -1)
          .map(({ value, label }) => ({ value, label }))
      },
    },

    methods: {
      close () {
        this.$store.commit(UI_MUTATION_TYPES.HIDE_MODAL, MODAL_IDS.ADD_PRODUCT_FROM_PROFILE)
      },

      validate () {
        this.errors = {}
        if (!this.form.name?.trim()) {
          this.errors.name = this.$t('validation_required', 'This field is required')
        }
        if (this.nonProductClaims.length > 1 && !this.selectedParentId) {
          this.errors.parentId = this.$t('profile_select_parent_actor', 'Please select an organisation to link this product to')
        }
        return Object.keys(this.errors).length === 0
      },

      async submit () {
        if (!this.validate()) return

        this.busy = true

        const parentId = this.selectedParentId ?? this.nonProductClaims[0]?.id ?? null

        const parentActor = parentId ? this.nonProductClaims.find(a => a.id === parentId) : null

        const contactDetails = {}
        if (this.keepAddress && parentActor) {
          if (parentActor.address && (parentActor.address.city || parentActor.address.street)) {
            contactDetails.address = { ...parentActor.address }
          }
          if (parentActor.phone) contactDetails.phone = parentActor.phone
          if (parentActor.email) contactDetails.email = parentActor.email
        }

        try {
          const actor = await createActor({
            id: null,
            actor_type: 'Product',
            name: this.form.name.trim(),
            actor_name: null,
            url: this.form.url || null,
            tags: [],
            ...(this.form.category ? { category: this.form.category } : {}),
            ...contactDetails,
          })

          if (!actor?.id) throw new Error('createActor did not return an actor')

          trackHeapEvent('global.addActor', { name: actor.name, id: actor.id })

          // Link the product to the parent actor via has_product relationship
          if (parentId) {
            const hasProduct = [
              ...(parentActor?.has_product || []),
              { to: actor.id, to_name: actor.name, start: null, end: null },
            ]
            await updateActor({ id: parentId, data: { has_product: hasProduct } })
          }

          // Refresh user profile so new product shows up
          this.$store.commit('USER/HAS_CLAIMED', actor)
          const profile = await fetchProfile()
          this.$store.commit('USER/UPDATE_PROFILE', profile)
          this.$bus.emit('updateUserProfile')

          this.close()
        } catch (err) {
          if (err?.errors) {
            this.errors = err.errors
          } else {
            console.error(err)
          }
        } finally {
          this.busy = false
        }
      },
    },

    mounted () {
      if (this.nonProductClaims.length === 1) {
        this.selectedParentId = this.nonProductClaims[0].id
      }
    },
  }
</script>

<style lang="scss" scoped>
  .pap-body {
    padding: 20px 20px 17px 20px;
  }

  .form-group {
    margin-bottom: 0;
  }

  .pap-actor-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

</style>
